import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@infotrack/zenith-ui';

import { useITopFiveApiV2EndpointsAuthMeEndpoint } from '@/api/api.generated';

interface RequireAuthProps {
  children: React.ReactNode;
  roles?: string[];
}

export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  roles = [],
}) => {
  const location = useLocation();

  const {
    data: authResponse,
    isLoading: isAuthLoading,
    error: authError,
  } = useITopFiveApiV2EndpointsAuthMeEndpoint({
    query: {
      retry: (failureCount, error) => {
        const isUnauthorizedError = (err: unknown): boolean => {
          if (!err || typeof err !== 'object') return false;
          const errorObj = err as {
            status?: number;
            response?: { status?: number };
          };
          return errorObj.status === 401 || errorObj.response?.status === 401;
        };

        const isAuthError = isUnauthorizedError(error);
        return !isAuthError && failureCount < 2;
      },
      retryDelay: 1000,
    },
  });

  // Show loading state while checking authentication
  if (isAuthLoading) {
    return <Spinner fullSize />;
  }

  // Redirect to login if not authenticated or auth error
  if (authError || !authResponse?.data) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required roles if specified
  if (roles.length > 0 && authResponse.data.claims) {
    const userRoles = Object.keys(authResponse.data.claims);
    const hasRequiredRole = roles.some(role => userRoles.includes(role));
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};
